import { motion } from "framer-motion";
import { Ticket, Sparkles, Crown, Megaphone, ChartNoAxesCombined } from "lucide-react";
import { Reveal, SectionHead, Eyebrow } from "@/components/Reveal";
import CountUp from "@/components/CountUp";
import CTABand from "@/components/CTABand";

const ease = [0.16, 1, 0.3, 1];

const KPIS = [
  { value: 72, suffix: "%", label: "MAU Growth Target", note: "Monthly active user growth" },
  { value: 58, suffix: "%", label: "CAC Efficiency", note: "Customer acquisition cost discipline" },
  { value: 85, suffix: "%", label: "LTV : CAC Benchmark", note: "Lifetime value vs. acquisition" },
  { value: 91, suffix: "%", label: "Ticket Conversion", note: "Ticket sales volume conversion" },
  { value: 78, suffix: "%", label: "Partner Retention", note: "Venues & promoters who stay" },
];

const REVENUE = [
  { icon: Ticket, title: "Ticketing Fees (5-15%)", desc: "Primary revenue from each ticket sold on the platform." },
  { icon: Sparkles, title: "Premium Event Placement", desc: "Sponsored visibility for promoters and venues." },
  { icon: Crown, title: "Subscription VIP Access", desc: "Exclusive perks and early-access tiers for repeat fans." },
  { icon: Megaphone, title: "Brand Sponsorships", desc: "Co-branded events and in-app advertising." },
  { icon: ChartNoAxesCombined, title: "B2B Data Insights", desc: "Analytics packages for venues and promoters." },
];

const CAPITAL = [
  { m: "Month 9", t: "Pricing optimization & Trinidad & Tobago pilot launch" },
  { m: "Month 10", t: "Additional US city pilots & investor outreach" },
  { m: "Month 11", t: "Seed funding close & infrastructure scaling" },
];

const Investors = () => (
  <div data-testid="page-investors" className="pt-28 sm:pt-32">
    <section className="bg-white">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <Eyebrow>Investor Relations</Eyebrow>
          <h1 className="mt-4 max-w-3xl font-display text-3xl font-black leading-[1.08] tracking-tight text-charcoal-900 sm:text-4xl lg:text-5xl">
            Backing the behavioral layer of live entertainment.
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-steel sm:text-lg">
            The platform that wins is not the one that sells the most tickets. It's the one that
            owns where people decide what to do. We are raising a seed round to fund MVP
            development, market launch, and team growth.
          </p>
        </Reveal>
      </div>
    </section>

    <section data-testid="ir-kpis" className="bg-charcoal-900 py-20 text-white sm:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead eyebrow="Key Success Metrics" title="KPIs tracking growth, retention & revenue performance." />
        <div className="mt-14 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {KPIS.map((k, i) => (
            <motion.div
              key={k.label}
              data-testid={`ir-kpi-card-${k.value}`}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: i * 0.06, ease }}
              className="h-full border border-line bg-surface p-7 transition-colors duration-300 hover:border-accent/50"
            >
              <div className="font-mono text-4xl font-semibold tracking-tight text-accent">
                <CountUp to={k.value} suffix={k.suffix} testId={`ir-counter-${k.value}`} />
              </div>
              <div className="mt-4 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-charcoal-900">{k.label}</div>
              <p className="mt-2 text-xs leading-relaxed text-steel">{k.note}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    <section data-testid="ir-revenue" className="bg-surface py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead eyebrow="Revenue Streams" title="Five diversified lines of revenue." lead="Ticketing is the monetization, and discovery is the moat." />
        <div className="mt-14 grid grid-cols-1 gap-px overflow-hidden border border-line bg-line md:grid-cols-5">
          {REVENUE.map((r, i) => (
            <Reveal key={r.title} delay={i * 0.05}>
              <div data-testid={`revenue-stream-${i}`} className="flex h-full flex-col bg-white p-7 transition-colors duration-300 hover:bg-accent-subtle/40">
                <r.icon className="h-5 w-5 text-accent" strokeWidth={1.8} />
                <h3 className="mt-8 font-display text-base font-bold leading-snug text-charcoal-900">{r.title}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-steel">{r.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section data-testid="ir-capital" className="bg-white py-20 sm:py-28">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-5 sm:px-8 lg:grid-cols-2">
        <div>
          <SectionHead eyebrow="Use of Capital" title="Where the seed round goes." lead="Funding MVP development, market launch, and team growth through the first twelve months." />
        </div>
        <div className="space-y-4">
          {CAPITAL.map((c, i) => (
            <Reveal key={c.m} delay={i * 0.06}>
              <div data-testid={`capital-item-${i}`} className="flex items-baseline gap-6 border border-line bg-surface p-6">
                <span className="w-20 shrink-0 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-accent">{c.m}</span>
                <span className="text-sm font-medium leading-relaxed text-charcoal-900">{c.t}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <CTABand testid="investors-cta" />
  </div>
);

export default Investors;
